/* eslint-disable react/no-multi-comp, react/display-name, react/prop-types */

import React from 'react'
import PropTypes from 'prop-types'
import ReactMarkdown from 'react-markdown'
import RemarkBreaks from 'remark-breaks'
import commentMarker from 'mdast-comment-marker'
import visit from 'unist-util-visit'
import { Paragraph, Heading } from '@scentregroup/ui'
import { startsWith, deburr, get, kebabCase } from 'lodash'
import ResponsiveImage from '../responsive-image'
import { ProductsInMarkdown } from '../products-in-markdown'
import Link from '../link'
import style from './index.scss'

const DEFAULT_MARKDOWN_IMAGE_WIDTH = 1200

// Take an inline html comment like so:
//
// <!--products align=left feature-->
//
// And parse it as
//
// <!--[renderer] [...parameters]-->
//
// Where `renderer` is a react-markdown renderer, and ...parameters is an object
// passed to the renderer.
//
// NOTE: a parameter without a value (`feature` in the above example) will have
// its value set to `true`.
//
// NOTE 2: Any magic comment which does not match the given renderer name will
// be passed through unchanged. It's advised to use react-markdown's `skipHtml`
// prop to avoid it being emitted as a raw HTML comment.
//
// Example
// Given the above comment, usage might look like:
//
//  <ReactMarkdown
//    skipHtml
//    astPlugins={[magicComments('products')]}
//    renderers={{
//      products: ({ parameters }) => (
//        <ProductList align={parameters.align || 'center'} isFeature={parameters.feature} />
//      )
//    }}
//  />
const magicComments = renderer => ast => {
  // Walk to each "html" node and modify it inline
  visit(ast, 'html', node => {
    // Attempt to extract the special comment syntax from the node
    const marker = commentMarker(node)

    if (marker && marker.name === renderer) {
      // Force the type to match the renderer
      node.type = renderer

      // Pass through the parsed parameters
      node.parameters = marker.parameters
    }
  })

  // Have to return the "new" ast (which in this case is just the existing ast
  // object with modified data)
  return ast
}

const renderers = ({ dataTestId, paragraphType }) => ({
  root: ({ children }) => (
    <div className={style.Markdown} data-test-id={dataTestId}>
      {children}
    </div>
  ),
  link: ({ title, href, children }) =>
    startsWith(href, '#') ? (
      <a href={href} title={title}>
        {children}
      </a>
    ) : (
      <Link href={href} title={title}>
        {children}
      </Link>
    ),
  paragraph: ({ children }) => (
    <Paragraph type={paragraphType}>{children}</Paragraph>
  ),
  heading: ({ level, children }) => (
    <Heading
      type={
        {
          1: 'large',
          2: 'medium',
          3: 'small',
        }[level] || 'extrasmall'
      }
      id={kebabCase(deburr(get(children, '[0].props.value', '')))}
    >
      {children}
    </Heading>
  ),
  image: ({ src, alt }) => (
    <ResponsiveImage
      lazyLoad
      desktopImageUrl={src}
      mobileImageUrl={src}
      width={DEFAULT_MARKDOWN_IMAGE_WIDTH}
      alt={alt}
    />
  ),
  tableCell: ({ isHeader, children }) =>
    isHeader ? (
      <th>
        <Heading type="extrasmall">{children}</Heading>
      </th>
    ) : (
      <td>{children}</td>
    ),
  products: ({ parameters = {} }) => (
    <ProductsInMarkdown ids={parameters.ids} />
  ),
})

// https://github.com/rexxars/react-markdown/blob/8ad8a995024a4729f066d109382b10c47d513379/src/renderers.js#L8
const Markdown = ({
  content,
  dataTestId,
  paragraphType = 'large',
  customRenderers = _ => ({}),
}) => {
  const defaultRenderers = renderers({ dataTestId, paragraphType })

  return (
    <ReactMarkdown
      skipHtml
      source={content}
      plugins={[RemarkBreaks]}
      // NOTE: has to be an astPlugin as it visits the ast after it's parsed, and
      // makes modifications before being given to the AST-to-react tree walker
      astPlugins={[magicComments('products')]}
      className={style.Markdown}
      renderers={{
        ...defaultRenderers,
        ...customRenderers(defaultRenderers),
      }}
    />
  )
}

Markdown.propTypes = {
  content: PropTypes.string.isRequired,
  paragraphType: PropTypes.oneOf(['large', 'medium', 'small']),
  dataTestId: PropTypes.string,
}

export default Markdown
